import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint } from '../../utils/resize.utils';
import { Icon } from '../../general/icons/Icon';
import { allowScrollingOnBody, preventScrollingOnBody } from '../../utils/body-scroll.utils';

/**
 * Initializes the filter elements.
 */
class FilterModule implements ComponentObservable {
  /**
   * Selector of all elements that must be initialized in javascript.
   */
  componentSelector = '.filter';

  /**
   * Current breakpoint
   */
  breakpoint = null;

  /**
   * Last window width.
   */
  lastWindowWidth = -1;

  /**
   * Filter width on desktop
   */
  filterWidth = '340px';

  /**
   * Initialize all filters on the current page.
   * @param observe Boolean if the FilterModule should listen for changes in the DOM and initialize dynamically
   *   added elements
   */
  initializeAll(observe: boolean): void {
    const allComponents = document.querySelectorAll(this.componentSelector);
    for (let i = 0; i < allComponents.length; i++) {
      this.initialize(allComponents[i] as HTMLElement);
    }

    if (observe) {
      this.startListening();
    }
  }

  /**
   * Listen for changes in DOM and initialize elements when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Creates or removes the button to expand or close the filter depending on screen size
   * @param aside The filter aside element
   * @param sidebar The filter sidebar element
   */
  createOrRemoveExpandButton(aside: HTMLElement, sidebar: HTMLElement): void {
    let button = aside.querySelector<HTMLElement>('#filter-button');
    // Click listener to toggle expand button
    const onButtonClick = (): void => {
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      this.updateExpandButton(!isExpanded, button, sidebar);

      const filterElem = aside.parentElement;
      filterElem.classList.toggle('filter--open', !isExpanded);
    };
    // Show expand button only on tablet and desktop (or larger)
    if (this.breakpoint.isTablet || this.breakpoint.isDesktop) {
      if (!button) {
        // Create filter button button
        button = document.createElement('button');
        button.classList.add('button', 'button--icon-only');
        button.setAttribute('id', 'filter-button');
        const span = document.createElement('span');
        span.classList.add('button__icon-single');
        button.appendChild(span);
        aside.appendChild(button);

        // Add click listener
        button.addEventListener('click', onButtonClick, false);
      }
      // Set initial state depending on screen size
      this.updateExpandButton(this.breakpoint.isDesktop, button, sidebar);
    } else {
      // Remove button on mobile
      button && aside.removeChild(button);
    }
  }

  /**
   * Creates the mobile header
   * @param sidebar The sidebar filter element
   */
  createHeader(sidebar: HTMLElement): void {
    let header = sidebar.querySelector<HTMLElement>('.filter-header');
    if (!header) {
      const labels = sidebar.parentElement.parentElement.querySelector<HTMLElement>('.filter').dataset;
      header = document.createElement('div');
      header.classList.add('filter-header');
      const wrapper = document.createElement('span');
      // Reset filter link
      const resetFilter = document.createElement('a');
      resetFilter.classList.add('filter-header__reset-button');
      resetFilter.innerText = labels.labelReset;
      wrapper.appendChild(resetFilter);
      header.appendChild(wrapper);
      // Headline
      const headline = document.createElement('h4');
      headline.innerText = labels.labelFilter;
      const wrapper2 = wrapper.cloneNode(false);
      wrapper2.appendChild(headline);
      header.appendChild(wrapper2);
      // Close button
      const button = document.createElement('button');
      button.classList.add('button', 'button--icon-only', 'button--no-states');
      button.setAttribute('id', 'close-filter-button');
      const span = document.createElement('span');
      span.classList.add('button__icon-single');
      span.innerHTML = Icon.close;
      const closeFilter = () => {
        sidebar.setAttribute('aria-hidden', 'true');
        allowScrollingOnBody(true);
      };
      button.addEventListener('click', closeFilter);

      // Add ESC key listener to close filter
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && sidebar.getAttribute('aria-hidden') === 'false') {
          closeFilter();
        }
      };

      // Add the event listener to document when filter becomes visible
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
            const target = mutation.target as HTMLElement;
            if (target.getAttribute('aria-hidden') === 'false') {
              document.addEventListener('keydown', handleEscKey);
            } else {
              document.removeEventListener('keydown', handleEscKey);
            }
          }
        });
      });

      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });

      button.appendChild(span);
      const wrapper3 = wrapper.cloneNode(false);
      wrapper3.appendChild(button);
      header.appendChild(wrapper3);
      // Append header
      sidebar.appendChild(header);
    }
  }

  /**
   * Creates the mobile footer
   * @param sidebar The sidebar filter element
   */
  createFooter(sidebar: HTMLElement): void {
    let footer = sidebar.querySelector<HTMLElement>('.filter-footer');
    if (!footer) {
      const labels = sidebar.parentElement.parentElement.querySelector<HTMLElement>('.filter').dataset;
      footer = document.createElement('button');
      footer.classList.add('filter-footer', 'button', 'button--primary');
      const icon = document.createElement('span');
      icon.classList.add('button__icon-right');
      icon.innerHTML = Icon.rightArrow;
      const text = document.createElement('span');
      text.classList.add('button__text');
      text.innerText = labels.labelButton;
      footer.appendChild(text);
      footer.appendChild(icon);
      // Append footer
      sidebar.appendChild(footer);

      footer.addEventListener('click', () => {
        this.closeFilter();
      });
    }
  }

  /**
   * Sets the properties of the expand button and the filter aside depending on the expanded state
   * @param isExpanded Whether the filter is expanded or not
   * @param button The button to toggle the filter state
   * @param sidebar The filter sidebar element
   */
  updateExpandButton(isExpanded: boolean, button: HTMLElement, sidebar: HTMLElement): void {
    button.setAttribute('aria-expanded', isExpanded.toString());
    button.firstElementChild.innerHTML = isExpanded ? Icon.expanded : Icon.settings;
    sidebar.style.width = isExpanded ? this.filterWidth : '0px';
    sidebar.setAttribute('aria-hidden', (!isExpanded).toString());
  }

  /**
   * Initialize open filter trigger (button). Opens the sidebar on click only on mobile.
   * @param root The filter root element
   */
  initializeOpenFilterTrigger(root: HTMLElement): void {
    const filterId = root.getAttribute('id');
    const triggerElement = document.querySelector<HTMLElement>(`[data-filter-id='${filterId}']`);
    if (triggerElement) {
      triggerElement.addEventListener('click', () => {
        this.openFilter();
      });
    }
  }

  /**
   * Opens the filter.
   */
  openFilter(): void {
    const sidebar = document.querySelector('.filter-sidebar');

    if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
      sidebar.setAttribute('aria-hidden', 'false');
    }

    // Prevent scrolling on the body while the filter is open
    preventScrollingOnBody(true);
  }

  /**
   * Closes the filter.
   */
  closeFilter(): void {
    const sidebar = document.querySelector('.filter-sidebar');

    if (this.breakpoint.isMobilePortrait || this.breakpoint.isMobileLandscape) {
      sidebar.setAttribute('aria-hidden', 'true');
    }
    // Allow scrolling on the body when the filter is closed
    allowScrollingOnBody(true);
  }

  /**
   * Renders the filter depending on the screen size
   */
  updateFilter(): void {
    const aside = document.querySelector<HTMLElement>('.filter__aside');
    const sidebar = document.querySelector<HTMLElement>('.filter-sidebar');
    const hideFilter = !this.breakpoint.isTablet && !this.breakpoint.isDesktop;
    if (aside && sidebar) {
      sidebar.removeAttribute('style');
      sidebar.setAttribute('aria-hidden', hideFilter.toString());
      allowScrollingOnBody(true);
      this.createOrRemoveExpandButton(aside, sidebar);
      this.createHeader(sidebar);
      this.createFooter(sidebar);
    }
  }

  /**
   * Initialize the given filter element.
   * Sets the listener for resize.
   * @param root The filter root element that should be initialized
   */
  initialize(root: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, root);
    this.breakpoint = currentBreakpoint();
    this.updateFilter();
    this.initializeOpenFilterTrigger(root);

    // timeoutId for debounce mechanism
    let timeoutId = null;

    // Set window resize listener
    this.lastWindowWidth = window.innerWidth;
    const resizeListener = (): void => {
      // Prevent execution of previous setTimeout
      clearTimeout(timeoutId);
      // Change width from the state object after 150 milliseconds
      timeoutId = setTimeout(() => {
        this.breakpoint = currentBreakpoint();
        if (this.lastWindowWidth !== window.innerWidth) {
          // only update the filter on resize if the width changed. If only the height
          // changed (for example on mobile due to browser bars) then don't reload, which would close the filter
          this.updateFilter();
        }
      }, 500);
    };
    // Set resize listener
    window.addEventListener('resize', resizeListener);
  }
}

const filterModule = new FilterModule();
export { filterModule as filter };
